import React, { useCallback, useEffect, useMemo } from "react";

import { Autocomplete, Button, Drawer, Stack, TextField } from "@mui/material";
import { SimpleHeadMui } from "components/Simple-head/SimpleHeadMui";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { IApiServiceError, useDealActions, useReportsActions, useReportsMetaSelector } from "store";
import { CreateMeasurePayload, IAssetClass, Measure, MeasureGroup, UpdateMeasurePayload } from "store/types";
import apiErrorsToFormik from "utils/api-errors-to-formik";

import { FormulaEditor } from "./FormulaEditor";

const measureInitial: CreateMeasurePayload = {
  postfix: "",
  is_currency: true,
  is_global: true,
  is_global_value: false,
  is_negative: false,
  numerify: "",
  formula: "",
  calc_formula_cross_round: false,
  name: "",
  reduce_method_cross_round: "",
  reduce_method_cross_time: "",
  reduce_method_cross_deal: "",
  prime_role: null,
  distribution: null,
  measure_group_id: null,
  measure_group_name: null,
  is_absolute: false,
  description: null,
  asset_class_id: null
};

interface AssetClassOption {
  id: number;
  label: string;
}

interface Props {
  measure: CreateMeasurePayload | null;
  onClose: () => void;
  measuresGroups: MeasureGroup[] | undefined;
  assetClasses: IAssetClass[];
}

const booleanOptions = [
  { id: 0, name: "true" },
  { id: 1, name: "false" }
];

const reduceMethodOption = [
  { id: 0, name: "last" },
  { id: 1, name: "sum" }
];

const postfixOption = [
  { id: 0, name: "%" },
  { id: 1, name: "$" }
];

const numerifyOption = [
  { id: 0, name: "0,0" },
  { id: 1, name: "00,00" }
];

const getAutocompleteValue = (value: string | null | undefined, options: { id: number; name: string }[]) =>
  options.find(({ name }) => name === value) || { id: 10000, name: "" }; //TODO refactor this one, sometimes it return undefined(because of "find"), and this undefined turn autocomplete from controlled to uncontrolled

export const Form: React.FC<Props> = ({ measure, onClose, measuresGroups, assetClasses }) => {
  const assetClassOptions = useMemo<AssetClassOption[]>(() => {
    const options: AssetClassOption[] = [];
    for (const ac of assetClasses) {
      options.push({ id: ac.id, label: ac.name });
      for (const sub of ac.sub_asset_class ?? []) {
        options.push({ id: sub.id, label: `${ac.name} > ${sub.name}` });
      }
    }
    return options;
  }, [assetClasses]);
  const isCreating = measure === null || !measure.hasOwnProperty("id");

  const { updateMeasureAction, createMeasureAction, getPrimeRoles } = useDealActions();

  const { loading: updateLoading, action: updateMeasure } = useActionHook(updateMeasureAction, {
    onSuccess: onClose,
    onReject: (err: IApiServiceError) => {
      setErrors(apiErrorsToFormik(measureInitial, (err as IApiServiceError).errors));
    }
  });

  const { loading: createLoading, action: createMeasure } = useActionHook(createMeasureAction, {
    onSuccess: onClose,
    onReject: (err: IApiServiceError) => {
      setErrors(apiErrorsToFormik(measureInitial, (err as IApiServiceError).errors));
    }
  });

  // TODO: get prime roles instead
  const { action: getPrimeRolesAction, data: primeRoles } = useActionHook(getPrimeRoles);

  const { getMetaAction } = useReportsActions();
  const { measures: metaMeasures, calculatedFields } = useReportsMetaSelector();
  const formulaOptions = useMemo(() => [...metaMeasures, ...calculatedFields], [metaMeasures, calculatedFields]);

  useEffect(() => {
    getPrimeRolesAction();
    getMetaAction();
  }, []);

  const state: CreateMeasurePayload = useMemo(() => {
    const initialState: CreateMeasurePayload = {
      ...measureInitial
    };

    if (!measure) {
      return initialState;
    }

    // Pick characteristic values
    for (const key in measureInitial) {
      const value = measure[key as keyof CreateMeasurePayload];
      if (value !== null && value !== undefined) {
        initialState[key as keyof CreateMeasurePayload] = value as never;
      }
    }

    return { ...initialState };
  }, [measure]);

  const { values, setFieldValue, handleSubmit, setErrors, errors } = useFormik({
    initialValues: state,
    onSubmit: (values) => {
      const dataBeforeSend: Partial<CreateMeasurePayload> = { id: measure?.id };
      // Set characteristic values
      Object.keys(measureInitial).forEach((key) => {
        const value = values[key as keyof CreateMeasurePayload];
        if (value !== null && value !== undefined) {
          (dataBeforeSend as any)[key as keyof CreateMeasurePayload] = value; // TS issue
        }
      });

      if (isCreating) {
        // TS partial issue
        createMeasure(dataBeforeSend as Measure);
        return;
      }

      if (!measure || !("id" in measure)) {
        return;
      }

      if (Object.keys(dataBeforeSend).length < 2) {
        onClose();
        return;
      }
      // TS partial issue
      updateMeasure(dataBeforeSend as UpdateMeasurePayload);
    },
    enableReinitialize: true
  });

  const clearError = useCallback(
    (field: keyof CreateMeasurePayload) => {
      if (!errors[field]) return;
      const errorsCopy = { ...errors };
      delete errorsCopy[field];
      setErrors(errorsCopy);
    },
    [errors]
  );

  const setMeasureGroupValues = useCallback(
    (measureGroup: MeasureGroup) => {
      setFieldValue("is_currency", measureGroup.is_currency);
      setFieldValue("distribution", measureGroup.distribution);
      setFieldValue("postfix", measureGroup.postfix);
    },
    [setFieldValue]
  );

  const changeWithError = useCallback(
    (field: keyof CreateMeasurePayload, value: any) => {
      clearError(field);
      if (field === "measure_group_id") {
        setFieldValue("measure_group_id", value ?? null);
        if (value) {
          const measureGroup = measuresGroups?.find(({ id }) => id === value);
          if (measureGroup) {
            setMeasureGroupValues(measureGroup);
          }
        }
      } else {
        setFieldValue(field, value ?? "");
      }
    },
    [clearError, setFieldValue, measuresGroups]
  );

  useEffect(() => {
    //set value from group to values
    if (measure) {
      const measureGroup = measuresGroups?.find(({ id }) => id === measure?.measure_group_id);
      if (measureGroup) {
        setMeasureGroupValues(measureGroup);
      }
    }
  }, [measure]);

  return (
    <Drawer
      onClose={onClose}
      sx={{
        ".MuiPaper-root": {
          borderRadius: "0 25px 25px 0"
        }
      }}
      open={!!measure}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          // Fast styling, should be found another way to style forms
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          overflow: "hidden",
          borderRadius: "0 12px 12px 0",
          width: "430px",
          height: "100%"
        }}
      >
        <SimpleHeadMui onIconClick={onClose}>{isCreating ? "Add Measure" : "Edit Measure"}</SimpleHeadMui>
        <Stack sx={{ p: "30px", gap: "24px", flexGrow: 1, overflow: "auto" }} alignItems="stretch">
          <TextField
            error={!!errors.name}
            helperText={errors.name}
            label="Name"
            onChange={(e) => changeWithError("name", e.target.value)}
            name="name"
            value={values?.name}
          />
          {primeRoles && (
            <Autocomplete
              getOptionLabel={(option: { id: number; name: string }) => option.name}
              options={primeRoles.map((el, i) => ({ id: i, name: el }))}
              value={getAutocompleteValue(
                values?.prime_role,
                primeRoles.map((el, i) => ({ id: i, name: el }))
              )}
              onChange={(_, value) => changeWithError("prime_role", value?.name)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Prime Role"
                  name="prime role"
                  value={values.prime_role}
                  error={!!errors.prime_role}
                  helperText={errors.prime_role}
                />
              )}
            />
          )}
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={reduceMethodOption}
            value={getAutocompleteValue(values?.reduce_method_cross_deal, reduceMethodOption)}
            onChange={(_, value) => changeWithError("reduce_method_cross_deal", value?.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reduce Method Cross Deal"
                name="reduce_method_cross_deal"
                value={values.reduce_method_cross_deal}
                error={!!errors.reduce_method_cross_deal}
                helperText={errors.reduce_method_cross_deal}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={reduceMethodOption}
            value={getAutocompleteValue(values?.reduce_method_cross_round, reduceMethodOption)}
            onChange={(_, value) => changeWithError("reduce_method_cross_round", value?.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reduce Method Cross Round"
                name="reduce_method_cross_round"
                value={values.reduce_method_cross_round}
                error={!!errors.reduce_method_cross_round}
                helperText={errors.reduce_method_cross_round}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={reduceMethodOption}
            value={getAutocompleteValue(values?.reduce_method_cross_time, reduceMethodOption)}
            onChange={(_, value) => changeWithError("reduce_method_cross_time", value?.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reduce Method Cross Time"
                name="reduce_method_cross_time"
                value={values.reduce_method_cross_time}
                error={!!errors.reduce_method_cross_time}
                helperText={errors.reduce_method_cross_time}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values?.is_negative.toString(), booleanOptions)}
            onChange={(_, value) => changeWithError("is_negative", value.name === "true")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Is Negative"
                name="is_negative"
                value={values.is_negative}
                error={!!errors.is_negative}
                helperText={errors.is_negative}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values?.is_global_value.toString(), booleanOptions)}
            onChange={(_, value) => changeWithError("is_global_value", value.name === "true")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Is Global Value"
                name="is_global_value"
                value={values.is_global_value}
                error={!!errors.is_global_value}
                helperText={errors.is_global_value}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values?.is_global.toString(), booleanOptions)}
            onChange={(_, value) => {
              const isGlobal = value.name === "true";
              changeWithError("is_global", isGlobal);
              if (isGlobal) setFieldValue("asset_class_id", null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Is Global"
                name="is_global"
                value={values.is_global}
                error={!!errors.is_global}
                helperText={errors.is_global}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: AssetClassOption) => option.label}
            options={assetClassOptions}
            disabled={values.is_global}
            value={
              values.asset_class_id != null
                ? (assetClassOptions.find(({ id }) => id === values.asset_class_id) ?? {
                    id: values.asset_class_id,
                    label: String(values.asset_class_id)
                  })
                : null
            }
            onChange={(_, value) => changeWithError("asset_class_id", value?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset Class"
                name="asset_class_id"
                error={!!errors.asset_class_id}
                helperText={errors.asset_class_id}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values?.is_absolute.toString(), booleanOptions)}
            onChange={(_, value) => changeWithError("is_absolute", value.name === "true")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Is Absolute"
                name="is_absolute"
                value={values.is_absolute}
                error={!!errors.is_absolute}
                helperText={errors.is_absolute}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={numerifyOption}
            value={getAutocompleteValue(values?.numerify, numerifyOption)}
            onChange={(_, value) => changeWithError("numerify", value?.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Numerify"
                name="numerify"
                value={values.numerify}
                error={!!errors.numerify}
                helperText={errors.numerify}
              />
            )}
          />
          <FormulaEditor
            label="Calculations"
            value={values.formula ?? ""}
            onChange={(v) => changeWithError("formula", v)}
            options={formulaOptions}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values?.calc_formula_cross_round.toString(), booleanOptions)}
            onChange={(_, value) => changeWithError("calc_formula_cross_round", value?.name === "true")}
            disabled={!values?.formula}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Calculate Formula Cross Rounds"
                name="calc_formula_cross_round"
                value={values.calc_formula_cross_round}
                error={!!errors.calc_formula_cross_round}
                helperText={errors.calc_formula_cross_round}
              />
            )}
          />
          {measuresGroups && (
            <Autocomplete
              freeSolo
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
              options={measuresGroups}
              value={
                values.measure_group_id
                  ? (measuresGroups.find(({ id }) => values.measure_group_id === id) ?? null)
                  : (values.measure_group_name ?? null)
              }
              onChange={(_, value) => {
                if (!value) {
                  setFieldValue("measure_group_id", null);
                  setFieldValue("measure_group_name", null);
                } else if (typeof value === "string") {
                  setFieldValue("measure_group_id", null);
                  setFieldValue("measure_group_name", value);
                } else {
                  setFieldValue("measure_group_id", value.id);
                  setFieldValue("measure_group_name", null);
                }
              }}
              onInputChange={(_, inputValue, reason) => {
                if (reason === "input") {
                  setFieldValue("measure_group_id", null);
                  setFieldValue("measure_group_name", inputValue || null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Measure Group"
                  name="measure_group_id"
                  error={!!errors.measure_group_id}
                  helperText={errors.measure_group_id}
                />
              )}
            />
          )}

          <Autocomplete
            disableClearable
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            value={getAutocompleteValue(values?.is_currency.toString(), booleanOptions)}
            onChange={(_, value) => changeWithError("is_currency", value?.name === "true")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Is Currency"
                name="is_currency"
                value={values.is_currency}
                error={!!errors.is_currency}
                helperText={errors.is_currency}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={booleanOptions}
            disableClearable
            value={getAutocompleteValue(values ? values?.distribution?.toString() : "false", booleanOptions)}
            onChange={(_, value) => changeWithError("distribution", value.name === "true")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Distribution"
                name="distribution"
                value={values.distribution}
                error={!!errors.distribution}
                helperText={errors.distribution}
              />
            )}
          />
          <Autocomplete
            getOptionLabel={(option: { id: number; name: string }) => option.name}
            options={postfixOption}
            clearText=""
            value={getAutocompleteValue(values?.postfix, postfixOption)}
            onChange={(_, value) => changeWithError("postfix", value?.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Postfix"
                name="postfix"
                value={values?.postfix}
                error={!!errors.postfix}
                helperText={errors.postfix}
              />
            )}
          />
          <TextField
            error={!!errors.description}
            helperText={errors.description}
            label="Description"
            onChange={(e) => changeWithError("description", e.target.value || null)}
            name="description"
            value={values?.description || ""}
          />
        </Stack>
        <Stack
          sx={{ p: "24px", gap: "10px", borderTop: "1px solid #dae0e2" }}
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button disabled={updateLoading || createLoading} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={updateLoading || createLoading} type="submit" variant="contained">
            Save
          </Button>
        </Stack>
      </form>
    </Drawer>
  );
};
